import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardHeader } from '@/ui/Card';
import { Field, Input } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useCorreo, useGuardarCorreo, useProbarCorreo } from './api';

/**
 * Correo SMTP — la configuración del servidor de salida.
 *
 * ## Lo que cambia
 *
 * - **La contraseña ya no es obligatoria para cambiar lo demás.** El vanilla
 *   exigía volver a escribirla en cada guardado (`if (!body.smtp_password)
 *   return`), y el campo llegaba siempre vacío: corregir una errata en el nombre
 *   del remitente pedía tener la contraseña del buzón a mano. Ahora se envía
 *   solo si se escribió algo, y el texto de ayuda lo dice.
 * - **El resultado del último test se lee.** Estaba dentro de un `alert-info`
 *   con la fecha cruda del servidor; ahora va con su insignia según si funcionó
 *   y la hora en la zona de quien mira.
 * - Un fallo al cargar deja de ser silencioso: era `try{...}catch{}` y pintaba
 *   el formulario con todos los campos en blanco, que es indistinguible de «no
 *   hay nada configurado».
 */
export function Correo() {
  const consulta = useCorreo(true);
  const guardar = useGuardarCorreo();
  const probar = useProbarCorreo();

  const c = consulta.data;
  const [listo, setListo] = useState(false);
  const [host, setHost] = useState('');
  const [puerto, setPuerto] = useState('587');
  const [usuario, setUsuario] = useState('');
  const [clave, setClave] = useState('');
  const [correoRemitente, setCorreoRemitente] = useState('');
  const [nombreRemitente, setNombreRemitente] = useState('');

  if (!listo && c) {
    setHost(c.smtp_host ?? '');
    setPuerto(String(c.smtp_port ?? 587));
    setUsuario(c.smtp_user ?? '');
    setCorreoRemitente(c.from_email ?? '');
    setNombreRemitente(c.from_name ?? '');
    setListo(true);
  }

  if (consulta.isPending) return <SkeletonFilas filas={6} columnas={2} />;
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="la configuración de correo"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }

  const falta = !host.trim() || !usuario.trim() || !correoRemitente.trim() || !nombreRemitente.trim();

  return (
    <Card className="max-w-xl">
      <CardHeader
        titulo={<><i className="bi bi-envelope-at" aria-hidden="true" /> Configuración SMTP</>}
      />
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate({
            smtp_host: host.trim(),
            smtp_port: parseInt(puerto, 10) || 587,
            smtp_user: usuario.trim(),
            // Solo viaja si se escribió: el servidor conserva la que tenía.
            ...(clave ? { smtp_password: clave } : {}),
            from_email: correoRemitente.trim(),
            from_name: nombreRemitente.trim(),
          }, {
            onSuccess: () => {
              setClave('');
              Utils.toast('Configuración SMTP guardada', 'success');
            },
            onError: (e2) => Utils.toast((e2 as Error).message, 'error'),
          });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
          <Field label="Servidor SMTP">
            <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.gmail.com" />
          </Field>
          <Field label="Puerto">
            <Input
              type="number"
              inputMode="numeric"
              value={puerto}
              onChange={(e) => setPuerto(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Usuario SMTP">
          <Input type="email" value={usuario} onChange={(e) => setUsuario(e.target.value)} />
        </Field>

        <Field
          label="Contraseña"
          opcional
          ayuda={
            c?.smtp_host
              ? 'Ya hay una guardada. Déjala en blanco para conservarla; escribe una nueva solo si quieres cambiarla.'
              : 'La contraseña o clave de aplicación del buzón que envía.'
          }
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            placeholder="••••••••"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Correo del remitente">
            <Input
              type="email"
              value={correoRemitente}
              onChange={(e) => setCorreoRemitente(e.target.value)}
            />
          </Field>
          <Field label="Nombre del remitente">
            <Input
              value={nombreRemitente}
              onChange={(e) => setNombreRemitente(e.target.value)}
              placeholder="Bienestar Su Servicio"
            />
          </Field>
        </div>

        {c?.test_sent_at && (
          <p className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-sunken p-3 text-2">
            <Badge tone={/ok|éxito|exito|enviad/i.test(c.test_result ?? '') ? 'ok' : 'danger'}>
              Último test
            </Badge>
            <span className="text-muted">{Utils.fmtDatetime(c.test_sent_at)}</span>
            {c.test_result && <span className="text-ink">{c.test_result}</span>}
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            loading={probar.isPending}
            disabled={falta}
            razonDeshabilitado="Completa el servidor, el usuario y el remitente antes de probar."
            onClick={() => probar.mutate(undefined, {
              onSuccess: (r) => Utils.toast(
                r.success ? 'Correo de prueba enviado' : (r.message || 'No se pudo enviar'),
                r.success ? 'success' : 'error'),
              onError: (e2) => Utils.toast((e2 as Error).message, 'error'),
            })}
          >
            <i className="bi bi-activity" aria-hidden="true" /> Probar conexión
          </Button>
          <Button
            type="submit"
            loading={guardar.isPending}
            disabled={falta}
            razonDeshabilitado="Completa el servidor, el usuario y el remitente."
          >
            Guardar
          </Button>
        </div>
      </form>
    </Card>
  );
}
